import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Badge, Button, Card, CardContent, CardHeader, CardTitle,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@agion/ui'
import { Check, Copy, Database } from 'lucide-react'
import type {
  DbLoginResultCode,
  DbLoginsIssue,
  DbLoginsIssueCode,
  DbLoginsLogin,
  DbLoginsResult,
  DbLoginsVerdict,
  DbLoginUnparsedReason,
  InfraOperation,
} from '@/core/infra_types'

// Shows the server's V81 database-login verdict (agion/Agion#3268). The verdict,
// each login's result and the GRANT text come from infra-service; this panel
// only maps their codes to words and never derives a verdict of its own.

const P = 'pages:operations.dbLogins'
const SUCCEEDED = new Set(['completed', 'succeeded'])
const NOT_FINISHED = new Set(['failed', 'cancelled'])

type BadgeVariant = 'success' | 'error' | 'warning' | 'default'

const VERDICT_BADGES: Record<DbLoginsVerdict, BadgeVariant> = {
  pass: 'success',
  fail: 'error',
  incomplete: 'warning',
}

const LOGIN_RESULTS: Record<DbLoginResultCode, string> = {
  owner: 'owner',
  agion_member: 'agionMember',
  superuser: 'superuser',
  not_edge_database: 'notEdgeDatabase',
  edge_name_other_address: 'edgeNameOtherAddress',
  edge_database_unknown: 'edgeDatabaseUnknown',
  unparsed: 'unparsed',
  unchecked: 'unchecked',
  role_missing: 'roleMissing',
  member_without_inherit: 'memberWithoutInherit',
  not_owner_or_member: 'notOwnerOrMember',
}

const UNPARSED_REASONS: Record<DbLoginUnparsedReason, string> = {
  format: 'format',
  user: 'user',
  host: 'host',
  port: 'port',
  database: 'database',
  query: 'query',
  environment: 'environment',
}

const CATALOG_REASONS: Record<string, string> = {
  no_agent_host_login: 'catalogNoAgentHostLogin',
  psql_missing: 'catalogPsqlMissing',
  connect_or_query_failed: 'catalogConnectFailed',
}

function lookup<K extends string, V>(table: Record<K, V>, code: string | undefined): V | undefined {
  if (!code || !Object.prototype.hasOwnProperty.call(table, code)) return undefined
  return table[code as K]
}

function readDbLogins(operation: InfraOperation): DbLoginsResult | null {
  const value = operation.output?.['dbLogins']
  if (!value || typeof value !== 'object') return null
  const result = value as DbLoginsResult
  return typeof result.verdict === 'string' ? result : null
}

function isDbLoginsCheck(operation: InfraOperation): boolean {
  return operation.operationType === 'host_diagnostics' && operation.parameters?.['profile'] === 'db_logins'
}

export function HostDbLoginsPanel({ operation }: { operation: InfraOperation }) {
  const { t } = useTranslation(['pages'])
  const titleId = useId()

  if (!isDbLoginsCheck(operation)) return null
  const succeeded = SUCCEEDED.has(operation.status)
  if (!succeeded && !NOT_FINISHED.has(operation.status)) return null

  const result = succeeded ? readDbLogins(operation) : null

  return (
    <Card className="mb-4" role="region" aria-labelledby={titleId}>
      <CardHeader>
        <CardTitle id={titleId} className="flex items-center gap-2 text-base">
          <Database size={16} className="text-slate-500 dark:text-slate-400" />
          {t(`${P}.title`, 'Database logins (V81)')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!succeeded && (
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {t(`${P}.notFinished`, 'The check did not finish, so there is no verdict. Run it again.')}
          </p>
        )}
        {succeeded && !result && (
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {t(`${P}.noVerdict`, 'This infra-service does not report a verdict yet. Update the platform and run the check again.')}
          </p>
        )}
        {result && <DbLoginsResultView result={result} />}
      </CardContent>
    </Card>
  )
}

function DbLoginsResultView({ result }: { result: DbLoginsResult }) {
  const { t } = useTranslation(['pages'])
  const variant = lookup(VERDICT_BADGES, result.verdict)
  const verdict = variant ? result.verdict : undefined
  const logins = Array.isArray(result.logins) ? result.logins : []
  const issues = Array.isArray(result.issues) ? result.issues : []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={variant ?? 'default'}>
          {verdict ? t(`${P}.verdict.${verdict}`) : t(`${P}.unknown`, { code: result.verdict })}
        </Badge>
        {verdict && (
          <p className="text-sm text-slate-700 dark:text-slate-300">{t(`${P}.summary.${verdict}`)}</p>
        )}
      </div>

      {logins.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t(`${P}.columns.service`, 'Service')}</TableHead>
                <TableHead>{t(`${P}.columns.setting`, 'Setting')}</TableHead>
                <TableHead>{t(`${P}.columns.login`, 'Login')}</TableHead>
                <TableHead>{t(`${P}.columns.database`, 'Database')}</TableHead>
                <TableHead>{t(`${P}.columns.result`, 'Result')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logins.map((login) => (
                <LoginRow key={`${login.service}|${login.env}`} login={login} />
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t(`${P}.noLogins`, 'The host reported no database logins.')}
        </p>
      )}

      {issues.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-slate-500 dark:text-slate-400">
            {t(`${P}.issuesTitle`, 'Notes from the check')}
          </p>
          <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700 dark:text-slate-300">
            {issues.map((issue, index) => (
              <li key={`${issue.code}|${issue.service ?? ''}|${index}`}>{issueText(t, issue)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

type TFn = ReturnType<typeof useTranslation>['t']

function issueText(t: TFn, issue: DbLoginsIssue): string {
  const detail = issue.detail && issue.detail !== 'missing' ? issue.detail : undefined
  const service = issue.service ?? ''
  switch (issue.code as DbLoginsIssueCode) {
    case 'other_profile':
      return detail
        ? t(`${P}.issue.otherProfile`, { profile: detail })
        : t(`${P}.issue.otherProfileUnnamed`)
    case 'no_transcript':
      return t(`${P}.issue.noTranscript`)
    case 'collection_failed':
      return detail
        ? t(`${P}.issue.collectionFailed`, { code: detail })
        : t(`${P}.issue.collectionFailedNoCode`)
    case 'catalog_unavailable': {
      const reason = lookup(CATALOG_REASONS, detail)
      return t(`${P}.issue.${reason ?? 'catalogUnavailable'}`)
    }
    case 'service_env_unreadable':
      return t(`${P}.issue.serviceEnvUnreadable`, { service })
    case 'service_not_running':
      return t(`${P}.issue.serviceNotRunning`, { service })
    default:
      return t(`${P}.unknownIssue`, { code: issue.code })
  }
}

function LoginRow({ login }: { login: DbLoginsLogin }) {
  const { t } = useTranslation(['pages'])
  const resultKey = lookup(LOGIN_RESULTS, login.verdict)
  const reasonKey = login.verdict === 'unparsed' ? lookup(UNPARSED_REASONS, login.reason) : undefined
  // A Unix socket has no TCP port: the server sends socket_default for the
  // driver's default socket directory, or the socket directory path itself.
  const isSocket = login.host === 'socket_default' || (login.host?.startsWith('/') ?? false)
  const host = login.host === 'socket_default' ? t(`${P}.localSocket`, 'local socket') : login.host
  const address = host ? (login.port && !isSocket ? `${host}:${login.port}` : host) : undefined
  const database = login.database
    ? (address ? t(`${P}.databaseAt`, { database: login.database, address }) : login.database)
    : '—'

  return (
    <TableRow>
      <TableCell className="whitespace-nowrap">{login.service}</TableCell>
      <TableCell className="font-mono text-xs">{login.env}</TableCell>
      <TableCell className="font-mono text-xs">{login.user || '—'}</TableCell>
      <TableCell className="text-sm">{database}</TableCell>
      <TableCell className="min-w-[16rem] text-sm">
        <p>{resultKey ? t(`${P}.result.${resultKey}`) : t(`${P}.unknown`, { code: login.verdict })}</p>
        {login.verdict === 'unparsed' && (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {reasonKey ? t(`${P}.reason.${reasonKey}`) : t(`${P}.reason.unknown`)}
          </p>
        )}
        {login.verdict === 'role_missing' && (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t(`${P}.roleMissingHelp`)}</p>
        )}
        {login.fix && <FixBlock fix={login.fix} login={login.user ?? ''} />}
      </TableCell>
    </TableRow>
  )
}

function FixBlock({ fix, login }: { fix: string; login: string }) {
  const { t } = useTranslation(['pages'])
  const [copied, setCopied] = useState(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current)
  }, [])

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fix)
      setCopied(true)
      if (resetTimer.current) clearTimeout(resetTimer.current)
      resetTimer.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }, [fix])

  return (
    <div className="mt-2 space-y-1">
      <p className="text-xs text-slate-500 dark:text-slate-400">{t(`${P}.fixHelp`)}</p>
      <div className="flex items-start gap-2">
        <pre className="min-w-0 flex-1 overflow-x-auto rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
          {fix}
        </pre>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void copy()}
          aria-label={t(`${P}.copyFor`, { login })}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? t(`${P}.copied`, 'Copied') : t(`${P}.copy`, 'Copy')}
        </Button>
      </div>
    </div>
  )
}
